/**
 * @brief Fetches the async components a Blade page uses, so the page can be
 *        shown once they have rendered instead of before.
 *
 * Blade pages are one Vue template rooted at `#app`, kept hidden until the app
 * mounts. Nearly every component is registered with `defineAsyncComponent`, so
 * without this the page appears with its static text and then each component
 * (the properties' selects, the tabs around the history, the composer) pops in
 * on its own once its chunk arrives.
 *
 * Fetching only the components that occur in the page's template keeps the rest
 * of the 100+ chunks unloaded, as before.
 */
import type { App, Component } from 'vue'

/**
 * `defineAsyncComponent` exposes its loader as `__asyncLoader`; Vue's own
 * hydration calls it the same way, and once it has resolved the component
 * renders synchronously instead of as an empty placeholder.
 */
type AsyncWrapper = Component & { __asyncLoader?: () => Promise<unknown> }

/** A page should not stay hidden behind a chunk that never arrives. */
const DEFAULT_TIMEOUT_MS = 3000

/** The tag a component name is written as in an in-DOM template. */
export function tagFor(name: string): string {
  return name.replace(/\B([A-Z])/g, '-$1').toLowerCase()
}

/**
 * Every element name in the tree, including inside `<template>` elements,
 * whose children live in a separate fragment that querySelectorAll skips.
 */
export function tagsIn(root: ParentNode): Set<string> {
  const tags = new Set<string>()

  const visit = (node: ParentNode): void => {
    for (const element of Array.from(node.querySelectorAll('*'))) {
      tags.add(element.tagName.toLowerCase())

      if (element instanceof HTMLTemplateElement) {
        visit(element.content)
      }
    }
  }

  visit(root)

  return tags
}

/** The loaders of the registered async components the tree uses. */
export function loadersFor(
  components: Record<string, Component>,
  root: ParentNode,
): Array<() => Promise<unknown>> {
  const tags = tagsIn(root)

  return Object.entries(components)
    .filter(([name]) => tags.has(tagFor(name)))
    .map(([, component]) => (component as AsyncWrapper).__asyncLoader)
    .filter((loader): loader is () => Promise<unknown> => !!loader)
}

/**
 * Starts loading right away and resolves once the page's async components are
 * loaded, or after the timeout. Call it before mounting: the tags are read
 * from the template, which mounting replaces. A chunk that fails to load is
 * left to Vue to report when it renders.
 */
export function preloadAsyncComponents(
  app: App,
  root: ParentNode,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<void> {
  const loads = loadersFor(app._context.components, root).map((load) =>
    load().catch(() => undefined),
  )

  return Promise.race([
    Promise.all(loads).then(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ])
}
